import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';

/**
 * Practice CTAs above the community feed.
 * - card (md+): short copy + buttons (LinkedIn desktop promo density)
 * - strip (mobile): button row only — feed stays the hero (LinkedIn/Meta mobile)
 */
export default function HeroActionBar({
  onStartInterview,
  onReviewLast,
  lastInterviewId,
  variant = 'card',
}) {
  const isStrip = variant === 'strip';

  if (isStrip) {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        data-testid="hero-action-bar"
        data-variant="strip"
        sx={{ flexShrink: 0 }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={onStartInterview}
          data-testid="hero-start-interview"
          sx={{ textTransform: 'none', fontWeight: 700, px: 1.5, whiteSpace: 'nowrap' }}
        >
          Practice
        </Button>
        {lastInterviewId && onReviewLast && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplayIcon />}
            onClick={onReviewLast}
            data-testid="hero-review-last"
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Review
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Box
      data-testid="hero-action-bar"
      data-variant="card"
      sx={{
        mb: 2.5,
        py: 1.25,
        px: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        position: { md: 'static' },
        top: { md: 'auto' },
        zIndex: 'auto',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Ready to practice?
        </Typography>
        <Typography variant="caption" color="text.secondary">
          One focused mock interview beats scrolling — then come back to the feed.
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={onStartInterview}
          data-testid="hero-start-interview"
          sx={{ textTransform: 'none', fontWeight: 700, px: 2 }}
        >
          Start practice
        </Button>
        {lastInterviewId && onReviewLast && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReplayIcon />}
            onClick={onReviewLast}
            data-testid="hero-review-last"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Review last
          </Button>
        )}
      </Stack>
    </Box>
  );
}

HeroActionBar.propTypes = {
  onStartInterview: PropTypes.func.isRequired,
  onReviewLast: PropTypes.func,
  lastInterviewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.oneOf(['card', 'strip']),
};
